/** Hostname labels (without .com) for `*.${domain}.com` monitoring matchers. */
export const DEFAULT_MONITORING_DOMAINS = ["bystadium", "swagmagic"] as const;

/** Full URLs containing these paths will not receive RUM trace header injection. */
export const TRACING_EXCLUDED_URL_PATHS = [] as const;

export const getMonitoringWhitelistUrl = (domain: string) =>
  new RegExp(`^https://.*\\.${domain}\\.com`, "gi");

export const getMonitoringWhitelistUrls = (
  domains: string | readonly string[] = DEFAULT_MONITORING_DOMAINS,
) => {
  const list = typeof domains === "string" ? [domains] : [...domains];
  return list.map(getMonitoringWhitelistUrl);
};

/**
 * RUM `beforeSend` filter for error stacks (not Sentry `allowUrls` frame filenames).
 * Matches whitelist patterns anywhere in the stack; allows events with no stack (Sentry parity).
 */
export const stackMatchesMonitoringWhitelist = (
  stack: string | undefined,
  whitelist: readonly (string | RegExp)[],
): boolean => {
  if (!whitelist.length) {
    return true;
  }
  if (!stack) {
    return true;
  }
  return whitelist.some((matcher) => {
    if (matcher instanceof RegExp) {
      const source = matcher.source.startsWith("^") ? matcher.source.slice(1) : matcher.source;
      // No `g` flag — avoids lastIndex quirks; match domain anywhere in the stack string.
      const flags = matcher.flags.replace(/g/g, "");
      return new RegExp(source, flags).test(stack);
    }
    return stack.includes(matcher);
  });
};

/** Stack text from a RUM error event (JS stack, handling stack, React component stack). */
export const getRumErrorStackTextForWhitelist = (event: {
  error?: { stack?: string; handling_stack?: string };
  context?: {
    extra?: {
      info?: { componentStack?: string };
      componentStack?: string;
    };
  };
}): string | undefined => {
  const parts = [
    event?.error?.stack,
    event?.error?.handling_stack,
    event?.context?.extra?.info?.componentStack,
    event?.context?.extra?.componentStack,
  ].filter((value): value is string => Boolean(value));
  return parts.length > 0 ? parts.join("\n") : undefined;
};

export const rumErrorEventMatchesMonitoringWhitelist = (
  event: Parameters<typeof getRumErrorStackTextForWhitelist>[0],
  whitelist: readonly (string | RegExp)[],
): boolean => stackMatchesMonitoringWhitelist(getRumErrorStackTextForWhitelist(event), whitelist);

/** Static env reads — required for Next.js client bundle inlining. */
const getTracingOriginsFromEnv = (): string[] =>
  [
    process.env.PUBLIC_URL,
    process.env.NEXT_PUBLIC_API_BASE_URL,
    process.env.NEXT_PUBLIC_SM_API_BASE_URL,
    process.env.NEXT_PUBLIC_SM_API_HOST,
    process.env.NEXT_PUBLIC_PRODUCTS_GRAPHQL_API_BASE_URL,
    process.env.NEXT_PUBLIC_USER_SERVICE_API_BASE_URL,
    process.env.NEXT_PUBLIC_SEARCH_API_BASE_URL,
    process.env.NEXT_PUBLIC_REWARDS_API_BASE_URL,
    process.env.NEXT_PUBLIC_MFA_API_BASE_URL,
    process.env.REACT_APP_API_BASE_URL,
    process.env.REACT_APP_SM_API_BASE_URL,
  ].filter((url): url is string => Boolean(url?.trim()));

const matchesTracingMatcher = (url: string, matcher: string | RegExp): boolean => {
  if (matcher instanceof RegExp) {
    return matcher.test(url);
  }
  return url.startsWith(matcher);
};

/**
 * Origins RUM may inject trace headers into (distributed tracing).
 * Aligns with Sentry allowUrls: domain regexes + API hosts from env.
 * Excludes high-volume config endpoints (see TRACING_EXCLUDED_URL_PATHS).
 */
export const getDefaultAllowedTracingUrls = (
  domains: string | readonly string[] = DEFAULT_MONITORING_DOMAINS,
) => {
  const matchers = [...getMonitoringWhitelistUrls(domains), ...getTracingOriginsFromEnv()];

  return [
    (url: string) =>
      !TRACING_EXCLUDED_URL_PATHS.some((path) => url.includes(path)) &&
      matchers.some((matcher) => matchesTracingMatcher(url, matcher)),
  ];
};
